/**
 * CommunityDashboardHome — the volunteer-person-centric dashboard home.
 * Focus = the individual volunteer, NOT the community entity.
 *   §1 Greeting hero: "Welcome back, {volunteerName}" + TB-awareness subtitle.
 *   §2 Volunteer Activity Summary: 6 metric tiles from getMyStats() (honest —
 *      Volunteer Hours shows "Data required", never a fabricated number).
 *   §3 Today's Opportunities preview (top joinable events + campaigns).
 *   §4 Volunteer Interests selector (10 tags, saved via updateMyMemberProfile).
 *   + Quick-action links to the other sections.
 * No financial concepts anywhere. No fabricated statistics.
 */
import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Sparkles, Megaphone, Clock, Share2, GraduationCap, Activity as ActivityIcon,
  Compass, ListChecks, Users, FolderOpen, Award, Bell, Bot, UserCircle,
} from 'lucide-react'
import { CommunityLayout, GovCard } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { MetricTile, EventCard, CampaignCard, SectionLoading, SectionEmpty } from '@/components/community/ui'
import { useAuthStore } from '@/stores/authStore'
import {
  getMyStats, listEvents, listCommunityCampaigns, updateMyMemberProfile,
  type MyStats, type EventResponse, type CommunityCampaignResponse,
} from '@/services/communityApi'

// §4 — the 10 volunteer-interest tags (spec). Stored as the member's `interests`.
const INTEREST_TAGS = [
  'Awareness Drives', 'Community Outreach', 'Digital Awareness', 'College Outreach',
  'Family Support', 'Stigma Reduction', 'Content Creation', 'Event Planning',
  'Translation', 'Counselling',
]

const QUICK_LINKS = [
  { key: 'opportunities', path: '/community-dashboard/opportunities', icon: Compass, labelKey: 'nav.community.opportunities' },
  { key: 'myActivities', path: '/community-dashboard/my-activities', icon: ListChecks, labelKey: 'nav.community.myActivities' },
  { key: 'learning', path: '/community-dashboard/learning', icon: GraduationCap, labelKey: 'nav.community.learning' },
  { key: 'community', path: '/community-dashboard/community', icon: Users, labelKey: 'nav.community.community' },
  { key: 'resources', path: '/community-dashboard/resources', icon: FolderOpen, labelKey: 'nav.community.resources' },
  { key: 'badges', path: '/community-dashboard/badges', icon: Award, labelKey: 'nav.community.badges' },
  { key: 'certificates', path: '/community-dashboard/certificates', icon: Award, labelKey: 'nav.community.certificates' },
  { key: 'myProfile', path: '/community-dashboard/my-profile', icon: UserCircle, labelKey: 'nav.community.myProfile' },
]

export default function CommunityDashboardHome() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const { user } = useAuthStore()
  const [stats, setStats] = useState<MyStats | null>(null)
  const [events, setEvents] = useState<EventResponse[]>([])
  const [campaigns, setCampaigns] = useState<CommunityCampaignResponse[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.allSettled([
      getMyStats(),
      // Opportunities preview: upcoming events + active campaigns from the
      // volunteer's own community (community may be null on first paint).
      community ? listEvents(community.id, 'UPCOMING', 0, 3) : Promise.resolve({ content: [] } as any),
      community ? listCommunityCampaigns(community.id) : Promise.resolve([] as any),
    ]).then(([st, ev, ca]) => {
      if (!active) return
      if (st.status === 'fulfilled') setStats(st.value)
      if (ev.status === 'fulfilled') setEvents((ev.value as any).content ?? [])
      if (ca.status === 'fulfilled') setCampaigns((ca.value as CommunityCampaignResponse[]).slice(0, 3))
      setLoading(false)
    })
    return () => { active = false }
  }, [community])

  const volunteerName = user?.fullName ?? t('community.greeting.defaultName')

  return (
    <CommunityLayout>
      {/* §1 — Greeting hero */}
      <div className="mb-6 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 p-6 sm:p-8 text-white shadow-sm">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-medium text-blue-100 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" aria-hidden="true" />{t('community.greeting.eyebrow')}
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold leading-tight">
              {t('community.greeting.welcome', { name: volunteerName })}
            </h1>
            <p className="mt-2 text-sm sm:text-base text-blue-50 max-w-xl">
              {t('community.greeting.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/notifications" aria-label={t('nav.community.notifications')}
              className="p-2.5 rounded-xl bg-white/15 hover:bg-white/25 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <Bell className="w-5 h-5" aria-hidden="true" />
            </Link>
            <Link to="/assistant" aria-label={t('nav.community.assistant')}
              className="p-2.5 rounded-xl bg-white/15 hover:bg-white/25 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <Bot className="w-5 h-5" aria-hidden="true" />
            </Link>
            <Link to="/community-dashboard/my-profile" aria-label={t('nav.community.myProfile')}
              className="p-2.5 rounded-xl bg-white/15 hover:bg-white/25 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <UserCircle className="w-5 h-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>

      {/* §2 — Volunteer Activity Summary (6 cards) */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.stats.sectionTitle')}</h2>
      {loading ? (
        <SectionLoading rows={2} />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          <MetricTile icon={Megaphone} value={stats?.campaignsJoined ?? 0} labelKey="community.stats.campaignsJoined" />
          <MetricTile icon={Clock} value={stats?.volunteerHours ?? 0} labelKey="community.stats.volunteerHours" dataRequired={stats?.volunteerHoursDataRequired} />
          <MetricTile icon={ActivityIcon} value={stats?.awarenessActivities ?? 0} labelKey="community.stats.awarenessActivities" />
          <MetricTile icon={GraduationCap} value={stats?.learningModulesCompleted ?? 0} labelKey="community.stats.learningModulesCompleted" />
          <MetricTile icon={Share2} value={stats?.resourcesShared ?? 0} labelKey="community.stats.resourcesShared" />
          <MetricTile icon={Users} value={stats?.communityActivities ?? 0} labelKey="community.stats.communityActivities" />
        </div>
      )}

      {/* §3 — Today's Opportunities preview */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700">{t('community.opportunities.previewTitle')}</h2>
        <Link to="/community-dashboard/opportunities" className="text-xs font-medium text-blue-600 hover:underline">
          {t('community.actions.viewAll')}
        </Link>
      </div>
      {loading ? <SectionLoading rows={2} /> : (events.length === 0 && campaigns.length === 0) ? (
        <SectionEmpty icon="🧭" title={t('community.opportunities.none')} />
      ) : (
        <div className="space-y-3 mb-8">
          {events.map((e) => <EventCard key={`e${e.id}`} event={e} />)}
          {campaigns.map((c) => <CampaignCard key={`c${c.id}`} campaign={c} />)}
        </div>
      )}

      {/* §4 — Volunteer Interests selector */}
      <InterestsSelector />

      {/* Quick-action links */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3 mt-8">{t('community.dashboard.quickActions')}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {QUICK_LINKS.map((a) => {
          const Icon = a.icon
          return (
            <Link key={a.key} to={a.path}>
              <GovCard className="h-full">
                <div className="flex flex-col items-center text-center gap-2 py-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{t(a.labelKey)}</span>
                </div>
              </GovCard>
            </Link>
          )
        })}
      </div>
    </CommunityLayout>
  )
}

/** §4 — selectable interest tags; toggles persist to the member profile. */
function InterestsSelector() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  const load = useCallback(async () => {
    // The member profile (interests) is not exposed by /community/my; load the
    // community members list and find the current user. If that fails, start empty.
    if (!community) return
    try {
      const { getMembers } = await import('@/services/communityApi')
      const { useAuthStore } = await import('@/stores/authStore')
      const me = useAuthStore.getState().user
      const members = await getMembers(community.id)
      const mine = members.find((m) => m.user.id === me?.id)
      setSelected(mine?.interests ?? [])
    } catch { /* ignore — start empty */ }
    finally { setLoading(false) }
  }, [community])

  useEffect(() => { void load() }, [load])

  const toggle = (tag: string) => {
    setSelected((p) => p.includes(tag) ? p.filter((x) => x !== tag) : [...p, tag])
    setDirty(true)
  }

  const save = async () => {
    setSaving(true)
    try {
      await updateMyMemberProfile({ interests: selected })
      setDirty(false)
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  return (
    <GovCard>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700">{t('community.interests.title')}</h2>
        {dirty && (
          <button onClick={save} disabled={saving}
            className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
            {saving ? t('common.saving') : t('community.interests.save')}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">{t('community.interests.subtitle')}</p>
      {loading ? <SectionLoading rows={1} /> : (
        <div className="flex flex-wrap gap-2">
          {INTEREST_TAGS.map((tag) => {
            const on = selected.includes(tag)
            return (
              <button key={tag} type="button" onClick={() => toggle(tag)} aria-pressed={on}
                className={`text-sm font-medium px-3 py-1.5 rounded-full border min-h-[40px] transition-colors ${
                  on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                }`}>
                {tag}
              </button>
            )
          })}
        </div>
      )}
    </GovCard>
  )
}